import { formatNumberWithCommas } from '@copmer/calculator-integration/shared';
import { CadenceType, GoFrcApi, SubActivityType, VoyageResult } from '@copmer/frc-api-types';
import { Fragment } from 'react';

import { useCalculatorCurrency, useCalculatorCurrencySign, useConvertCurrency } from './currency-context';
import { DebugTableKeyVal, DraftDepthRenderer } from './DebugTable';
import DottedLineImage from './dotted-line.svg';
import Icon from './Icon';
import { getSubActivityLabel } from './labels';
import { valueSuffixConstructor } from './utils';

type PortStageT = VoyageResult['debugData']['stages'][0] & {
    portCallIndex: number | null;
};

const PortItem = ({
    stage,
    plan,
    isLastStage,
    waterTypes,
}: {
    stage: PortStageT;
    plan: VoyageResult;
    isLastStage?: boolean;
    waterTypes: GoFrcApi.GoFRCReferenceDataRoutesWaterTypeResponse[];
}) => {
    const convertCurrency = useConvertCurrency();
    const currency = useCalculatorCurrency();
    const currencySign = useCalculatorCurrencySign();

    const maturity = plan.debugData.inputParams.baseDate;

    const portCallIndex = stage.portCallIndex;

    if (portCallIndex === null || portCallIndex === undefined) {
        return null;
    }

    const portCall = plan.debugData.portInfos[portCallIndex];

    if (!portCall) {
        return null;
    }

    const costSum = stage.subactivities.reduce((acc, subactivity) => acc + subactivity.sumInDollars, 0);

    return (
        <>
            {portCall.operations.map((operation, i) => (
                <Fragment key={i}>
                    <div className="grid grid-cols-4 gap-y-2 gap-x-3 max-[400px]:grid-cols-2 max-[320px]:grid-cols-1 relative">
                        {isLastStage && i === portCall.operations.length - 1 ? (
                            <div className="w-6 absolute -left-9 bottom-0 bg-frGrey-990 top-0" />
                        ) : null}

                        <DebugTableKeyVal
                            value={
                                <>
                                    {portCall.DisplayName}

                                    <div
                                        className="w-6 absolute -left-9 top-0 bg-frGrey-990 py-2"
                                        title={`isLastStage: ${isLastStage}, i: ${i}, portCall.operations.length: ${portCall.operations.length}`}
                                    >
                                        <Icon
                                            icon={operation.operation === 'L' ? 'Download' : 'Upload'}
                                            className="text-frGrey-300 h-6 w-6 -mt-2"
                                        />
                                    </div>
                                </>
                            }
                            labelClassName="leading-5"
                            valueClassName="relative"
                            label={
                                <>
                                    {operation.operation === 'L' ? 'Load' : 'Discharge'}{' '}
                                    {formatNumberWithCommas(operation.amount)}
                                    <br className="max-[400px]:hidden" />
                                    <span className="max-[400px]:inline hidden"> </span>
                                    {operation.commodity}
                                </>
                            }
                            className="col-span-2 row-span-2 max-[400px]:col-span-1 max-[400px]:row-span-1"
                            grow="end"
                        />

                        <DebugTableKeyVal
                            value={
                                operation?.cadenceInputType === CadenceType.HOURS
                                    ? `${formatNumberWithCommas(operation.cadence)} hours`
                                    : formatNumberWithCommas(operation.cadence)
                            }
                            label="Cadence"
                        />
                        <DebugTableKeyVal value={operation.terms} label="Terms" />

                        {operation.draft <= 25 ? (
                            <DebugTableKeyVal
                                value={
                                    <DraftDepthRenderer
                                        draft={operation.draft}
                                        draftDetails={operation.draftDetails}
                                        waterTypes={waterTypes}
                                    />
                                }
                                label="Draft"
                            />
                        ) : null}

                        <DebugTableKeyVal
                            value={valueSuffixConstructor(
                                formatNumberWithCommas(
                                    convertCurrency(
                                        operation.portDA,
                                        currency,
                                        maturity,
                                        operation.portDACurrency,
                                        plan.currencyRates
                                    )
                                ),
                                currencySign
                            )}
                            label="Port DA"
                        />
                    </div>
                </Fragment>
            ))}
        </>
    );
};

const SailingItem = ({
    stage,
    isLastStage,
}: {
    stage: VoyageResult['debugData']['stages'][0];
    isLastStage?: boolean;
}) => {
    const currencySign = useCalculatorCurrencySign();

    return (
        <>
            {stage.subactivities
                .filter(
                    (item) =>
                        ![SubActivityType.VesselHire, SubActivityType.EU_ETS, SubActivityType.TotalCost].includes(
                            item.subActivityType as SubActivityType
                        )
                )
                .map((subactivity, j) => (
                    <div
                        className="grid grid-cols-4 gap-y-2 gap-x-3 max-[400px]:grid-cols-1"
                        key={subactivity.subactivity + j}
                    >
                        <DebugTableKeyVal
                            value={
                                <>
                                    {subactivity.subactivity}

                                    <div className="w-6 absolute -left-9 top-0 bg-frGrey-990 py-2">
                                        <Icon icon="Circle" className="text-frGrey-300 h-6 w-6 -mt-2" />
                                    </div>

                                    {subactivity.details && subactivity.details.length > 0 ? (
                                        <div className="text-sm mt-2 font-light text-frGrey-400 leading-none grid grid-cols-2 grid-cols-[50%_30%]">
                                            {subactivity.details.map((detail) => (
                                                <Fragment key={detail.label}>
                                                    <span className="whitespace-nowrap">{detail.label}:</span>
                                                    <span className="text-right">
                                                        {valueSuffixConstructor(
                                                            formatNumberWithCommas(detail.value, true),
                                                            detail.suffix ?? ''
                                                        )}
                                                    </span>
                                                </Fragment>
                                            ))}
                                        </div>
                                    ) : null}
                                </>
                            }
                            label={getSubActivityLabel(subactivity.subActivityType)}
                            valueClassName="relative"
                            className="col-span-3 max-[400px]:col-span-1"
                        />

                        <DebugTableKeyVal
                            value={valueSuffixConstructor(
                                formatNumberWithCommas(subactivity.sumInDollars),
                                currencySign
                            )}
                            label={<>&nbsp;</>}
                        />
                    </div>
                ))}
        </>
    );
};

export default function MobileDebugTable({
    plan,
    waterTypes,
}: {
    plan: VoyageResult;
    waterTypes: GoFrcApi.GoFRCReferenceDataRoutesWaterTypeResponse[];
}) {
    const stages = plan.debugData.stages.filter((stage) => stage.name !== 'Generic');

    return (
        <div className="flex flex-col gap-6 py-4 pl-7 relative">
            <div
                className="absolute top-5 bottom-4 -left-2 pointer-none w-6"
                style={{
                    background: `url(${DottedLineImage.src}) repeat-y center top`,
                }}
            />
            {stages.map((stage, i) =>
                stage.name === 'Port' ? (
                    <PortItem
                        key={i}
                        stage={stage as PortStageT}
                        plan={plan}
                        isLastStage={i === stages.length - 1}
                        waterTypes={waterTypes}
                    />
                ) : stage.name.startsWith('Sailing') ? (
                    <SailingItem key={i} stage={stage} isLastStage={i === stages.length - 1} />
                ) : null
            )}
        </div>
    );
}
